// =======================================================
// ACCESSIBILITY WIDGET - Gestione accessibilità frontend
// =======================================================

interface AccessibilityWidgetState {
    fontSizeStep: number;
    fontFamilyStep: number;
    spacingStep: number;
    interlineStep: number;
    highTitle: boolean;
    highLink: boolean;
    hideImg: boolean;
    cursorBig: boolean;
    readingGuide: boolean;
    readingMask: boolean;
    grayscale: boolean;
    inverti: boolean;
    saturazioneStep: number;
    brightnessStep: number;
    contrastStep: number;
    hueStep: number;
    animationDisabled: boolean;
    dislessiaProfile: boolean;
    daltonismoProfile: boolean;
    epilessiaProfile: boolean;
    adhdProfile: boolean;
    ipovedentiProfile: boolean;
}

interface HeadingNode {
    el: HTMLHeadingElement;
    text: string;
    idx: number;
    level: number;
    children: HeadingNode[];
}

interface HeadingRoot {
    children: HeadingNode[];
    level: number;
}

interface SemanticNode {
    el: Element;
    tag: string;
    idx: number;
    children: SemanticNode[];
}

interface LinkInfo {
    el: HTMLAnchorElement;
    idx: number;
    href: string;
    text: string;
    isExternal: boolean;
    hasAriaLabel: boolean;
    ariaLabel: string;
}

// Helper per impostare proprietà vendor-prefixed su CSSStyleDeclaration
function setVendorFilter(el: HTMLElement | HTMLHtmlElement, value: string): void {
    const style = el.style as any;
    style.filter = value;
    style.webkitFilter = value;
    style.mozFilter = value;
    style.msFilter = value;
    style.oFilter = value;
}

function appendVendorFilter(el: HTMLElement | HTMLHtmlElement, filterValue: string): void {
    const style = el.style as any;
    style.filter       = appendFilter(style.filter       || '', filterValue);
    style.webkitFilter = appendFilter(style.webkitFilter || '', filterValue);
    style.mozFilter    = appendFilter(style.mozFilter    || '', filterValue);
    style.msFilter     = appendFilter(style.msFilter     || '', filterValue);
    style.oFilter      = appendFilter(style.oFilter      || '', filterValue);
}

function removeVendorFilterPart(el: HTMLElement | HTMLHtmlElement, pattern: RegExp): void {
    const style = el.style as any;
    ['filter', 'webkitFilter', 'mozFilter', 'msFilter', 'oFilter'].forEach(prop => {
        let val: string = style[prop] || '';
        val = val.replace(pattern, '').replace(/\s{2,}/g, ' ').trim();
        style[prop] = val;
    });
}

function appendFilter(current: string, filter: string): string {
    if (!current) return filter;
    if (current.includes(filter)) return current;
    return current.trim() + ' ' + filter;
}

// ==============================
// 5. STRUTTURA PAGINA E MODALI
// ==============================

// --- Titoli: costruzione e rendering albero ---
function buildHeadingsTree(): HeadingNode[] {
    const headings = Array.from(document.body.querySelectorAll<HTMLHeadingElement>('h1, h2, h3, h4, h5, h6'))
        .filter(h => !h.closest('.modal') && !h.closest('.offcanvas'));
    const root: HeadingRoot = { children: [], level: 0 };
    const stack: (HeadingRoot | HeadingNode)[] = [root];
    let idx = 0;

    headings.forEach(h => {
        h.setAttribute('data-aw-heading-idx', String(idx));
        const level = parseInt(h.tagName[1], 10);
        const node: HeadingNode = {
            el: h,
            text: (h.textContent || '').trim(),
            idx: idx,
            level: level,
            children: []
        };
        idx++;
        while (stack.length > 1 && (stack[stack.length - 1] as HeadingNode).level >= level) {
            stack.pop();
        }
        stack[stack.length - 1].children.push(node);
        stack.push(node);
    });
    return root.children;
}

function isHeadingVisible(node: HeadingNode): boolean {
    const el = node.el;
    const carouselItem = el.closest('.carousel-item');
    if (carouselItem) {
        return carouselItem.classList.contains('active');
    }
    const swiperSlide = el.closest('.swiper-slide');
    if (swiperSlide) {
        return swiperSlide.classList.contains('swiper-slide-active') || swiperSlide.classList.contains('swiper-slide-visible');
    }
    return el.offsetParent !== null;
}

function renderHeadingsTree(nodes: HeadingNode[]): string {
    if (!nodes.length) return '';
    let html = '<ul class="list-unstyled ms-2 lh-lg">';
    nodes.forEach(node => {
        const tag = node.el.tagName.toLowerCase();
        const visible = isHeadingVisible(node);
        html += `<li>
            <span class="badge text-bg-secondary me-2">${tag}</span>
            <a href="#" class="aw-heading-link link-dark" data-aw-heading-idx="${node.idx}">${node.text}</a>
            ${visible ? '  ' : ' <span title="Nascosto" class="material-symbols-outlined">visibility_off</span> '}
            ${renderHeadingsTree(node.children)}
        </li>`;
    });
    html += '</ul>';
    return html;
}

// --- Sezioni semantiche: costruzione e rendering albero ---
function buildSemanticTree(): SemanticNode | null {
    const semanticTags = ['html', 'body', 'main', 'header', 'nav', 'aside', 'section', 'article', 'footer', 'form'];
    let idx = 0;

    function buildNode(el: Element): SemanticNode | null {
        if (!semanticTags.includes(el.tagName.toLowerCase())) return null;
        el.setAttribute('data-aw-semantic-idx', String(idx));
        const node: SemanticNode = {
            el: el,
            tag: el.tagName.toLowerCase(),
            idx: idx,
            children: []
        };
        idx++;
        Array.from(el.children).forEach(child => {
            const childNode = buildNode(child);
            if (childNode) node.children.push(childNode);
        });
        return node;
    }

    return buildNode(document.documentElement);
}

function isSectionVisible(node: SemanticNode): boolean {
    const el = node.el as HTMLElement;
    return el.offsetParent !== null;
}

function renderSemanticTree(node: SemanticNode | null): string {
    if (!node) return '';
    const visible = isSectionVisible(node);
    let html = `<ul class="list-unstyled ms-2 lh-lg"><li>
        <span class="badge text-bg-secondary me-2">
            <span class="material-symbols-outlined">${visible ? 'code' : 'code_off'}</span>
        </span>
        <a href="#" class="aw-semantic-link link-dark" data-aw-semantic-idx="${node.idx}">${node.tag}</a>`;
    if (node.children.length) {
        node.children.forEach(child => {
            html += renderSemanticTree(child);
        });
    }
    html += '</li></ul>';
    return html;
}

// --- Links: raccolta e rendering ---
function getAllLinksInfo(): LinkInfo[] {
    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('a:not(.modal a):not(.offcanvas a):not(.dropdown a)'))
        .filter(a =>
            !a.closest('.modal') &&
            !a.closest('.offcanvas') &&
            !a.closest('.dropdown')
        );
    return links.map((a, idx) => {
        const href = a.getAttribute('href') || '';
        const text = (a.textContent || '').trim() || href;
        const isExternal = /^https?:\/\//i.test(href) && !href.includes(location.hostname);
        const hasAriaLabel = a.hasAttribute('aria-label');
        const ariaLabel = a.getAttribute('aria-label') || '';
        return { el: a, idx, href, text, isExternal, hasAriaLabel, ariaLabel };
    });
}

function renderLinksList(links: LinkInfo[]): string {
    if (!links.length) return '<p>Nessun link trovato.</p>';
    let html = '<ul class="list-unstyled ms-2 lh-lg">';
    links.forEach(link => {
        html += `<li class="mb-2 d-flex align-items-center">
            <span class="badge text-bg-secondary me-2">
                <span class="material-symbols-outlined align-middle">link</span>
            </span>
            <a href="${link.href}" class="aw-link-item link-dark" target="_blank" rel="noopener" data-aw-link-idx="${link.idx}" title="${link.ariaLabel ? link.ariaLabel : link.text}">
                ${link.text}
            </a>
            ${link.isExternal ? '<span class="ms-2 material-symbols-outlined" title="Link esterno">open_in_new</span>' : ''}
            ${link.hasAriaLabel ? '<span class="ms-2 material-symbols-outlined" title="Ha aria-label">label</span>' : ''}
        </li>`;
    });
    html += '</ul>';
    return html;
}

// ==============================
// EXPORT PRINCIPALE
// ==============================

export function initAccessibilityWidget(): void {

    // ==============================
    // 1. INIZIALIZZAZIONE VARIABILI
    // ==============================

    // Mappa per le dimensioni originali dei titoli (usata per il resize reversibile)
    const originalHeadingSizes = new Map<Element, number>();

    // --- Font Size ---
    const increaseFontSizeBtn = document.getElementById('aW-increaseTxt');
    const sizeCounter = document.getElementById('aW-size-counter');
    const stepFontSizeContainer = document.getElementById('aW-size-step');
    const stepFontSizeBoxes: NodeListOf<Element> = stepFontSizeContainer ? stepFontSizeContainer.querySelectorAll('.border') : document.querySelectorAll('__none__');
    const stepFontSizePercents = [10, 20, 33, 45, 60];
    const headingFontSizePercents = [5, 10, 15, 20, 25];
    const maxFontSizeSteps = stepFontSizePercents.length;
    const baseFontSizePx = 16;
    let currentFontSizeStep = 0;
    const headings = document.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5, .h6');
    headings.forEach(el => {
        originalHeadingSizes.set(el, parseFloat(window.getComputedStyle(el).fontSize));
    });

    // --- Font Family ---
    const fontFamilyBtn = document.getElementById('aW-font');
    const stepFontFamilyContainer = document.getElementById('aW-font-step');
    const stepFontFamilyBoxes: NodeListOf<Element> = stepFontFamilyContainer ? stepFontFamilyContainer.querySelectorAll('.border') : document.querySelectorAll('__none__');
    const fontFamilyCounter = document.getElementById('aW-font-counter');
    const fontFamilyLabels = ['testo normale', 'dislessia', 'testi leggibili'];
    const fontFamilyValues = ['', "'openDyslexic', Arial, sans-serif", 'Helvetica, Arial, sans-serif'];
    const maxFontFamilySteps = fontFamilyLabels.length;
    let currentFontFamilyStep = 0;

    // --- Spaziatura ---
    const spacingBtn = document.getElementById('aW-spacing');
    const stepSpacingContainer = document.getElementById('aW-spacing-step');
    const stepSpacingBoxes: NodeListOf<Element> = stepSpacingContainer ? stepSpacingContainer.querySelectorAll('.border') : document.querySelectorAll('__none__');
    const spacingCounter = document.getElementById('aW-spacing-counter');
    const spacingLabels = ['+0%', '+10%', '+20%', '+45%'];
    const spacingValues = [
        { word: '', letter: '' },
        { word: '0.16rem', letter: '0.12rem' },
        { word: '0.32rem', letter: '0.24rem' },
        { word: '0.48rem', letter: '0.36rem' }
    ];
    const maxSpacingSteps = spacingLabels.length;
    let currentSpacingStep = 0;

    // --- Interlinea ---
    const interlineBtn = document.getElementById('aW-interline');
    const stepInterlineContainer = document.getElementById('aW-interline-step');
    const stepInterlineBoxes: NodeListOf<Element> = stepInterlineContainer ? stepInterlineContainer.querySelectorAll('.border') : document.querySelectorAll('__none__');
    const interlineCounter = document.getElementById('aW-interline-counter');
    const interlineLabels = ['+0%', '1.75X', '2X', '2.25X'];
    const interlineValues = ['', '1.75', '2', '2.25'];
    const maxInterlineSteps = interlineLabels.length;
    let currentInterlineStep = 0;

    // --- Evidenzia Titoli e Link ---
    const highTitleBtn = document.getElementById('aW-highTitle');
    const highLinkBtn = document.getElementById('aW-highLink');
    let isHighTitleActive = false;
    let isHighLinkActive = false;

    // --- Nascondi Immagini ---
    const hideImgBtn = document.getElementById('aW-hideImg');
    let isHideImgActive = false;

    // --- Cursore grande ---
    const cursorBtn = document.getElementById('aW-cursor');
    let isCursorBig = false;

    // --- Guida alla lettura ---
    const readingGuideBtn = document.getElementById('aW-readingGuide');
    let isReadingGuideActive = false;
    let readingGuideLine: HTMLDivElement | null = null;
    let readingGuideTriangle: HTMLDivElement | null = null;
    let readingGuideMouseHandler: ((e: MouseEvent) => void) | null = null;

    // --- Maschera di lettura ---
    const readingMaskBtn = document.getElementById('aW-readingMask');
    let isReadingMaskActive = false;
    let readingMaskOverlay: HTMLDivElement | null = null;
    let readingMaskMouseHandler: ((e: MouseEvent) => void) | null = null;

    // --- Scala di grigio ---
    const grayscaleBtn = document.getElementById('aW-grayscale');
    let isGrayscaleActive = false;

    // --- Inverti colori ---
    const invertiBtn = document.getElementById('aW-inverti');
    let isInvertiActive = false;

    // --- Saturazione ---
    const saturazioneBtn = document.getElementById('aW-saturazione');
    const saturazioneCounter = document.getElementById('aW-saturazione-counter');
    const stepSaturazioneContainer = document.getElementById('aW-saturazione-step');
    const stepSaturazioneBoxes: NodeListOf<Element> = stepSaturazioneContainer ? stepSaturazioneContainer.querySelectorAll('.border') : document.querySelectorAll('__none__');
    const saturazioneLabels = ['100%', '150%', '200%', '20%', '60%'];
    const saturazioneValues = ['saturate(1)', 'saturate(1.5)', 'saturate(2)', 'saturate(0.2)', 'saturate(0.6)'];
    const stepToBox: (number | null)[] = [null, 2, 3, 0, 1];
    const maxSaturazioneSteps = 5;
    let currentSaturazioneStep = 0;

    // --- Luminosità ---
    const brightnessBtn = document.getElementById('aW-brightness');
    const brightnessCounter = document.getElementById('aW-brightness-counter');
    const stepBrightnessContainer = document.getElementById('aW-brightness-step');
    const stepBrightnessBoxes: NodeListOf<Element> = stepBrightnessContainer ? stepBrightnessContainer.querySelectorAll('.border') : document.querySelectorAll('__none__');
    const brightnessLabels = ['100%', '125%', '150%', '50%', '75%'];
    const brightnessValues = ['brightness(1)', 'brightness(1.25)', 'brightness(1.5)', 'brightness(0.5)', 'brightness(0.75)'];
    const stepToBoxBrightness: (number | null)[] = [null, 2, 3, 0, 1];
    const maxBrightnessSteps = 5;
    let currentBrightnessStep = 0;

    // --- Contrasto ---
    const contrastBtn = document.getElementById('aW-contrast');
    const contrastCounter = document.getElementById('aW-contrast-counter');
    const stepContrastContainer = document.getElementById('aW-contrast-step');
    const stepContrastBoxes: NodeListOf<Element> = stepContrastContainer ? stepContrastContainer.querySelectorAll('.border') : document.querySelectorAll('__none__');
    const contrastLabels = ['100%', '125%', '150%', '50%', '75%'];
    const contrastValues = ['contrast(1)', 'contrast(1.25)', 'contrast(1.5)', 'contrast(0.5)', 'contrast(0.75)'];
    const stepToBoxContrast: (number | null)[] = [null, 2, 3, 0, 1];
    const maxContrastSteps = 5;
    let currentContrastStep = 0;

    // --- Tonalità (Hue-rotate) ---
    const hueBtn = document.getElementById('aW-hue');
    const hueCounter = document.getElementById('aW-hue-counter');
    const stepHueContainer = document.getElementById('aW-hue-step');
    const stepHueBoxes: NodeListOf<Element> = stepHueContainer ? stepHueContainer.querySelectorAll('.border') : document.querySelectorAll('__none__');
    const hueLabels = ['0°', '45°', '90°', '135°', '180°', '225°', '270°', '315°', '360°'];
    const hueValues = [
        'hue-rotate(0deg)', 'hue-rotate(45deg)', 'hue-rotate(90deg)', 'hue-rotate(135deg)', 'hue-rotate(180deg)',
        'hue-rotate(225deg)', 'hue-rotate(270deg)', 'hue-rotate(315deg)', 'hue-rotate(360deg)'
    ];
    const stepToBoxHue: (number | null)[] = [null, 0, 1, 2, 3, 4, 5, 6, 7];
    const maxHueSteps = 9;
    let currentHueStep = 0;

    // --- Animazioni ---
    const animationBtn = document.getElementById('aW-animation');
    let isAnimationDisabled = false;

    // --- Sintesi vocale ---
    let isReadingModeActive = false;

    // --- Reset ---
    const resetBtn = document.getElementById('aw-reset');
    if (resetBtn) {
        resetBtn.addEventListener('click', function (e) {
            e.preventDefault();
            resetAccessibilityWidget();
        });
    }

    // --- Profilo Dislessia ---
    const dislessiaBtn = document.getElementById('aW-dislessia');
    let isDislessiaProfileActive = false;

    // --- Profilo Daltonismo ---
    const daltonismoBtn = document.getElementById('aW-daltonismo');
    let isDaltonismoProfileActive = false;

    // --- Profilo Epilessia ---
    const epilessiaBtn = document.getElementById('aW-epilessia');
    let isEpilessiaProfileActive = false;

    // --- Profilo ADHD ---
    const adhdBtn = document.getElementById('aW-adhd');
    let isAdhdProfileActive = false;

    // --- Profilo Ipovedenti ---
    const ipovedentiBtn = document.getElementById('aW-ipovedenti');
    let isIpovedentiProfileActive = false;


    // ==============================
    // 2. FUNZIONI DI STATO E STORAGE
    // ==============================

    function saveAccessibilityState(): void {
        const state: AccessibilityWidgetState = {
            fontSizeStep: currentFontSizeStep,
            fontFamilyStep: currentFontFamilyStep,
            spacingStep: currentSpacingStep,
            interlineStep: currentInterlineStep,
            highTitle: isHighTitleActive,
            highLink: isHighLinkActive,
            hideImg: isHideImgActive,
            cursorBig: isCursorBig,
            readingGuide: isReadingGuideActive,
            readingMask: isReadingMaskActive,
            grayscale: isGrayscaleActive,
            inverti: isInvertiActive,
            saturazioneStep: currentSaturazioneStep,
            brightnessStep: currentBrightnessStep,
            contrastStep: currentContrastStep,
            hueStep: currentHueStep,
            animationDisabled: isAnimationDisabled,
            dislessiaProfile: isDislessiaProfileActive,
            daltonismoProfile: isDaltonismoProfileActive,
            epilessiaProfile: isEpilessiaProfileActive,
            adhdProfile: isAdhdProfileActive,
            ipovedentiProfile: isIpovedentiProfileActive
        };
        localStorage.setItem('accessibilityWidgetState', JSON.stringify(state));
    }

    function loadAccessibilityState(): void {
        const raw = localStorage.getItem('accessibilityWidgetState');
        const state: Partial<AccessibilityWidgetState> | null = raw ? JSON.parse(raw) : null;
        if (state) {
            currentFontSizeStep    = typeof state.fontSizeStep    === 'number' ? state.fontSizeStep    : 0;
            currentFontFamilyStep  = typeof state.fontFamilyStep  === 'number' ? state.fontFamilyStep  : 0;
            currentSpacingStep     = typeof state.spacingStep     === 'number' ? state.spacingStep     : 0;
            currentInterlineStep   = typeof state.interlineStep   === 'number' ? state.interlineStep   : 0;
            isHighTitleActive      = !!state.highTitle;
            isHighLinkActive       = !!state.highLink;
            isHideImgActive        = !!state.hideImg;
            isCursorBig            = !!state.cursorBig;
            isReadingGuideActive   = !!state.readingGuide;
            isReadingMaskActive    = !!state.readingMask;
            isGrayscaleActive      = !!state.grayscale;
            isInvertiActive        = !!state.inverti;
            currentSaturazioneStep = typeof state.saturazioneStep === 'number' ? state.saturazioneStep : 0;
            currentBrightnessStep  = typeof state.brightnessStep  === 'number' ? state.brightnessStep  : 0;
            currentContrastStep    = typeof state.contrastStep    === 'number' ? state.contrastStep    : 0;
            currentHueStep         = typeof state.hueStep         === 'number' ? state.hueStep         : 0;
            isAnimationDisabled    = !!state.animationDisabled;
            isDislessiaProfileActive  = !!state.dislessiaProfile;
            updateDislessiaProfileActiveClass();
            isDaltonismoProfileActive = !!state.daltonismoProfile;
            updateDaltonismoProfileActiveClass();
            isEpilessiaProfileActive  = !!state.epilessiaProfile;
            updateEpilessiaProfileActiveClass();
            isAdhdProfileActive       = !!state.adhdProfile;
            updateAdhdProfileActiveClass();
            isIpovedentiProfileActive = !!state.ipovedentiProfile;
            updateIpovedentiProfileActiveClass();
        }
        // Applica le impostazioni del profilo SENZA chiamare toggle che fa reset!
        if (isDislessiaProfileActive) {
            isAnimationDisabled   = true;
            currentFontFamilyStep = 1;
            currentSpacingStep    = 1;
            currentInterlineStep  = 1;
            applyAnimationToggle();
            updateFontFamilyUI();
            applyFontFamily();
            updateSpacingUI();
            applySpacing();
            updateInterlineUI();
            applyInterline();
            updateDislessiaProfileActiveClass();
            updateToggleIndicator();
        }
        if (isDaltonismoProfileActive) {
            currentSaturazioneStep = 1;
            currentContrastStep    = 1;
            currentHueStep         = 5;
            updateSaturazioneUI();
            applySaturazione();
            updateContrastUI();
            applyContrast();
            updateHueUI();
            applyHue();
            updateDaltonismoProfileActiveClass();
            updateToggleIndicator();
        }
        if (isEpilessiaProfileActive) {
            isAnimationDisabled    = true;
            currentSaturazioneStep = 4;
            applyAnimationToggle();
            updateSaturazioneUI();
            applySaturazione();
            updateEpilessiaProfileActiveClass();
            updateToggleIndicator();
        }
        if (isAdhdProfileActive) {
            isAnimationDisabled    = true;
            currentSaturazioneStep = 4;
            isReadingMaskActive    = true;
            applyAnimationToggle();
            updateSaturazioneUI();
            applySaturazione();
            applyReadingMask();
            updateAdhdProfileActiveClass();
            updateToggleIndicator();
        }
        if (isIpovedentiProfileActive) {
            isAnimationDisabled    = true;
            currentFontSizeStep    = 3;
            currentFontFamilyStep  = 2;
            isCursorBig            = true;
            currentSaturazioneStep = 1;
            applyAnimationToggle();
            updateFontSizeUI();
            setFontSize();
            updateFontFamilyUI();
            applyFontFamily();
            applyBigCursor();
            updateSaturazioneUI();
            applySaturazione();
            updateIpovedentiProfileActiveClass();
            updateToggleIndicator();
        }
    }


    // ==============================
    // 3. FUNZIONI UI E LOGICA
    // ==============================

    /* --------------------------------------------------------------------------
       3.1. FONT SIZE - Gestione aumento/ripristino dimensione testo
    ----------------------------------------------------------------------------*/
    function updateFontSizeUI(): void {
        if (!sizeCounter || !stepFontSizeContainer) return;
        sizeCounter.classList.remove('d-none');
        stepFontSizeContainer.classList.remove('d-none');
        stepFontSizeContainer.classList.add('d-flex');
        const percent = currentFontSizeStep > 0 ? stepFontSizePercents[currentFontSizeStep - 1] : 0;
        sizeCounter.textContent = `+ ${percent}%`;
        stepFontSizeBoxes.forEach((el, idx) => {
            el.classList.toggle('border-primary', idx < currentFontSizeStep);
        });
        updateFontSizeActiveClass();
        updateToggleIndicator();
    }

    function setFontSize(): void {
        const percent        = currentFontSizeStep > 0 ? stepFontSizePercents[currentFontSizeStep - 1]        : 0;
        const headingPercent = currentFontSizeStep > 0 ? headingFontSizePercents[currentFontSizeStep - 1] : 0;
        const newFontSize    = baseFontSizePx * (1 + percent / 100);
        const excluded = [
            document.getElementById('offcanvasAccessibilita'),
            document.getElementById('accessibilityToggle')
        ];
        function isExcluded(el: Element): boolean {
            return excluded.some(ex => ex && (ex === el || ex.contains(el)));
        }
        Array.from(document.body.children).forEach(el => {
            (el as HTMLElement).style.fontSize = !isExcluded(el) ? `${newFontSize}px` : '';
        });
        headings.forEach(el => {
            if (!isExcluded(el)) {
                const originalPx = originalHeadingSizes.get(el) || 0;
                const newPx = originalPx * (1 + headingPercent / 100);
                el.style.fontSize = `${newPx}px`;
            } else {
                el.style.fontSize = '';
            }
        });
    }

    function updateFontSizeActiveClass(): void {
        const btn    = document.getElementById('aW-increaseTxt');
        const parent = btn ? btn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentFontSizeStep > 0);
        }
    }

    function increaseFontSize(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        currentFontSizeStep = (currentFontSizeStep + 1) % (maxFontSizeSteps + 1);
        updateFontSizeUI();
        setFontSize();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.2. FONT FAMILY - Gestione cambio font per leggibilità/dislessia
    ----------------------------------------------------------------------------*/
    function updateFontFamilyUI(): void {
        stepFontFamilyBoxes.forEach((el, idx) => {
            el.classList.toggle('border-primary', idx < currentFontFamilyStep);
        });
        if (fontFamilyCounter) fontFamilyCounter.textContent = fontFamilyLabels[currentFontFamilyStep];
        updateFontFamilyActiveClass();
    }

    function applyFontFamily(): void {
        const excludeSelectors = ['#accessibilityToggle', '#offcanvasAccessibilita'];
        Array.from(document.body.children).forEach(el => {
            if (!excludeSelectors.some(sel => el.matches(sel) || el.closest(sel))) {
                (el as HTMLElement).style.fontFamily = fontFamilyValues[currentFontFamilyStep];
            } else {
                (el as HTMLElement).style.fontFamily = '';
            }
        });
    }

    function updateFontFamilyActiveClass(): void {
        const btn    = document.getElementById('aW-font');
        const parent = btn ? btn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentFontFamilyStep > 0);
        }
    }

    function nextFontFamilyStep(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        currentFontFamilyStep = (currentFontFamilyStep + 1) % maxFontFamilySteps;
        updateFontFamilyUI();
        applyFontFamily();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.3. SPAZIATURA - Gestione spaziatura tra lettere e parole
    ----------------------------------------------------------------------------*/
    function updateSpacingUI(): void {
        if (spacingCounter) spacingCounter.textContent = spacingLabels[currentSpacingStep];
        stepSpacingBoxes.forEach((el, idx) => {
            el.classList.toggle('border-primary', idx < currentSpacingStep);
        });
        updateSpacingActiveClass();
        updateToggleIndicator();
    }

    function applySpacing(): void {
        const excluded = [
            document.getElementById('offcanvasAccessibilita'),
            document.getElementById('accessibilityToggle')
        ];
        function isExcluded(el: Element): boolean {
            return excluded.some(ex => ex && (ex === el || ex.contains(el)));
        }
        Array.from(document.body.children).forEach(el => {
            if (!isExcluded(el)) {
                (el as HTMLElement).style.wordSpacing   = spacingValues[currentSpacingStep].word;
                (el as HTMLElement).style.letterSpacing = spacingValues[currentSpacingStep].letter;
            } else {
                (el as HTMLElement).style.wordSpacing   = '';
                (el as HTMLElement).style.letterSpacing = '';
            }
        });
    }

    function updateSpacingActiveClass(): void {
        const btn    = document.getElementById('aW-spacing');
        const parent = btn ? btn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentSpacingStep > 0);
        }
    }

    function nextSpacingStep(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        currentSpacingStep = (currentSpacingStep + 1) % maxSpacingSteps;
        updateSpacingUI();
        applySpacing();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.4. INTERLINEA - Gestione altezza linea testo
    ----------------------------------------------------------------------------*/
    function updateInterlineUI(): void {
        if (interlineCounter) interlineCounter.textContent = interlineLabels[currentInterlineStep];
        stepInterlineBoxes.forEach((el, idx) => {
            el.classList.toggle('border-primary', idx < currentInterlineStep);
        });
        updateInterlineActiveClass();
        updateToggleIndicator();
    }

    function applyInterline(): void {
        const root = document.documentElement;
        if (currentInterlineStep > 0 && interlineValues[currentInterlineStep]) {
            root.style.setProperty('--bs-body-line-height', interlineValues[currentInterlineStep]);
        } else {
            root.style.removeProperty('--bs-body-line-height');
        }
        headings.forEach(el => {
            el.style.lineHeight = currentInterlineStep > 0 ? interlineValues[currentInterlineStep] : '';
        });
    }

    function updateInterlineActiveClass(): void {
        const btn    = document.getElementById('aW-interline');
        const parent = btn ? btn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentInterlineStep > 0);
        }
    }

    function nextInterlineStep(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        currentInterlineStep = (currentInterlineStep + 1) % maxInterlineSteps;
        updateInterlineUI();
        applyInterline();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.5. EVIDENZIAZIONE TITOLI E LINK - Gestione evidenziazione visuale
    ----------------------------------------------------------------------------*/
    function applyHighlightToTitles(): void {
        const excluded = [
            document.getElementById('offcanvasAccessibilita'),
            document.getElementById('accessibilityToggle')
        ];
        function isExcluded(el: Element): boolean {
            return excluded.some(ex => ex && (ex === el || ex.contains(el)));
        }
        document.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6').forEach(el => {
            if (!isExcluded(el)) {
                if (isHighTitleActive) {
                    el.style.setProperty('outline',          '2px solid var(--highlight-color)', 'important');
                    el.style.setProperty('outline-offset',   '2px',          'important');
                    el.style.setProperty('text-decoration',  'underline',    'important');
                    el.style.setProperty('background',       'yellow',       'important');
                    el.style.setProperty('color',            'black',        'important');
                } else {
                    el.style.removeProperty('outline');
                    el.style.removeProperty('outline-offset');
                    el.style.removeProperty('text-decoration');
                    el.style.removeProperty('background');
                    el.style.removeProperty('color');
                }
            }
        });
        const parent = highTitleBtn ? highTitleBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isHighTitleActive);
        }
    }

    function applyHighlightToLinks(): void {
        const excluded = [
            document.getElementById('offcanvasAccessibilita'),
            document.getElementById('accessibilityToggle')
        ];
        function isExcluded(el: Element): boolean {
            return excluded.some(ex => ex && (ex === el || ex.contains(el)));
        }
        document.querySelectorAll<HTMLElement>('a').forEach(el => {
            if (!isExcluded(el)) {
                if (isHighLinkActive) {
                    el.style.setProperty('outline',          '2px solid var(--highlight-color)', 'important');
                    el.style.setProperty('outline-offset',   '2px',          'important');
                    el.style.setProperty('text-decoration',  'underline',    'important');
                    el.style.setProperty('background',       'black',        'important');
                    el.style.setProperty('color',            'yellow',       'important');
                } else {
                    el.style.removeProperty('outline');
                    el.style.removeProperty('outline-offset');
                    el.style.removeProperty('text-decoration');
                    el.style.removeProperty('background');
                    el.style.removeProperty('color');
                }
            }
        });
        const parent = highLinkBtn ? highLinkBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isHighLinkActive);
        }
    }

    function toggleHighTitle(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isHighTitleActive = !isHighTitleActive;
        applyHighlightToTitles();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    function toggleHighLink(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isHighLinkActive = !isHighLinkActive;
        applyHighlightToLinks();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.6. NASCONDI IMMAGINI - Gestione visibilità immagini
    ----------------------------------------------------------------------------*/
    function applyHideImg(): void {
        const excluded = [
            document.getElementById('offcanvasAccessibilita'),
            document.getElementById('accessibilityToggle')
        ];
        function isExcluded(el: Element): boolean {
            return excluded.some(ex => ex && (ex === el || ex.contains(el)));
        }
        document.querySelectorAll('img').forEach(el => {
            if (!isExcluded(el)) {
                el.style.setProperty('visibility', isHideImgActive ? 'hidden' : '', 'important');
            }
        });
        const parent = hideImgBtn ? hideImgBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isHideImgActive);
        }
    }

    function toggleHideImg(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isHideImgActive = !isHideImgActive;
        applyHideImg();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.7. CURSORE GRANDE - Gestione cursore personalizzato
    ----------------------------------------------------------------------------*/
    function applyBigCursor(): void {
        document.body.classList.toggle('aW-cursor-big', isCursorBig);
        const parent = cursorBtn ? cursorBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isCursorBig);
        }
    }

    function toggleBigCursor(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isCursorBig = !isCursorBig;
        applyBigCursor();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.8. GUIDA ALLA LETTURA - Linea e triangolo che seguono il mouse
    ----------------------------------------------------------------------------*/
    function applyReadingGuide(): void {
        if (isReadingGuideActive) {
            if (!readingGuideLine) {
                readingGuideLine = document.createElement('div');
                readingGuideLine.className = 'aW-reading-guide-line';
                document.body.appendChild(readingGuideLine);
            }
            if (!readingGuideTriangle) {
                readingGuideTriangle = document.createElement('div');
                readingGuideTriangle.className = 'aW-reading-guide-triangle';
                document.body.appendChild(readingGuideTriangle);
            }
            if (!readingGuideMouseHandler) {
                readingGuideMouseHandler = (e: MouseEvent) => {
                    let el: Element | null = e.target as Element;
                    while (el) {
                        if ((el as HTMLElement).id === 'accessibilityToggle' || (el as HTMLElement).id === 'offcanvasAccessibilita') return;
                        el = el.parentElement;
                    }
                    readingGuideLine!.style.top         = `${e.clientY - 2}px`;
                    readingGuideTriangle!.style.left    = `${e.clientX - 12}px`;
                    readingGuideTriangle!.style.top     = `${e.clientY - 18}px`;
                };
                document.addEventListener('mousemove', readingGuideMouseHandler);
            }
            readingGuideLine.style.display    = 'block';
            readingGuideTriangle.style.display = 'block';
        } else {
            if (readingGuideLine)    readingGuideLine.style.display    = 'none';
            if (readingGuideTriangle) readingGuideTriangle.style.display = 'none';
            if (readingGuideMouseHandler) {
                document.removeEventListener('mousemove', readingGuideMouseHandler);
                readingGuideMouseHandler = null;
            }
        }
        const parent = readingGuideBtn ? readingGuideBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isReadingGuideActive);
        }
    }

    function toggleReadingGuide(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isReadingGuideActive = !isReadingGuideActive;
        applyReadingGuide();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.9. MASCHERA DI LETTURA - Overlay che segue il mouse
    ----------------------------------------------------------------------------*/
    function applyReadingMask(): void {
        if (isReadingMaskActive) {
            if (!readingMaskOverlay) {
                readingMaskOverlay = document.createElement('div');
                readingMaskOverlay.className = 'aW-reading-mask-overlay';
                document.body.appendChild(readingMaskOverlay);
            }
            if (!readingMaskMouseHandler) {
                readingMaskMouseHandler = (e: MouseEvent) => {
                    let el: Element | null = e.target as Element;
                    while (el) {
                        if ((el as HTMLElement).id === 'accessibilityToggle' || (el as HTMLElement).id === 'offcanvasAccessibilita') return;
                        el = el.parentElement;
                    }
                    const windowHeight = 300;
                    const top = Math.max(0, Math.min(window.innerHeight - windowHeight, e.clientY - windowHeight / 2));
                    const mask = `linear-gradient(to bottom, black ${top}px, transparent ${top}px, transparent ${top + windowHeight}px, black ${top + windowHeight}px)`;
                    const overlayStyle = readingMaskOverlay!.style as any;
                    overlayStyle.webkitMaskImage = mask;
                    overlayStyle.maskImage       = mask;
                };
                document.addEventListener('mousemove', readingMaskMouseHandler);
            }
            readingMaskOverlay.style.display = 'block';
        } else {
            if (readingMaskOverlay) readingMaskOverlay.style.display = 'none';
            if (readingMaskMouseHandler) {
                document.removeEventListener('mousemove', readingMaskMouseHandler);
                readingMaskMouseHandler = null;
            }
        }
        const parent = readingMaskBtn ? readingMaskBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isReadingMaskActive);
        }
    }

    function toggleReadingMask(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isReadingMaskActive = !isReadingMaskActive;
        applyReadingMask();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.10. FILTRI COLORI - Scala di grigio, inverti, saturazione, luminosità, contrasto, tonalità
    ----------------------------------------------------------------------------*/

    // Scala di grigio
    function applyGrayscale(): void {
        const html  = document.documentElement;
        const value = isGrayscaleActive ? 'grayscale(1)' : '';
        setVendorFilter(html, value);
        const parent = grayscaleBtn ? grayscaleBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isGrayscaleActive);
        }
    }

    function toggleGrayscale(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isGrayscaleActive = !isGrayscaleActive;
        applyGrayscale();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    // Inverti colori
    function applyInverti(): void {
        const html = document.documentElement;
        if (isInvertiActive) {
            appendVendorFilter(html, 'invert(1)');
        } else {
            removeVendorFilterPart(html, /invert\(1\)/g);
        }
        const parent = invertiBtn ? invertiBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isInvertiActive);
        }
    }

    function toggleInverti(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isInvertiActive = !isInvertiActive;
        applyInverti();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    // Saturazione
    function updateSaturazioneUI(): void {
        if (saturazioneCounter) saturazioneCounter.textContent = saturazioneLabels[currentSaturazioneStep];
        stepSaturazioneBoxes.forEach((el, idx) => {
            el.classList.remove('border-primary');
            if (currentSaturazioneStep > 0 && stepToBox[currentSaturazioneStep] === idx) {
                el.classList.add('border-primary');
            }
        });
        updateSaturazioneActiveClass();
        updateToggleIndicator();
    }

    function applySaturazione(): void {
        const html = document.documentElement;
        removeVendorFilterPart(html, /saturate\([^)]+\)/g);
        if (currentSaturazioneStep > 0) {
            appendVendorFilter(html, saturazioneValues[currentSaturazioneStep]);
        }
        const parent = saturazioneBtn ? saturazioneBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentSaturazioneStep > 0);
        }
    }

    function nextSaturazioneStep(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        currentSaturazioneStep = (currentSaturazioneStep + 1) % maxSaturazioneSteps;
        updateSaturazioneUI();
        applySaturazione();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    function updateSaturazioneActiveClass(): void {
        const parent = saturazioneBtn ? saturazioneBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentSaturazioneStep > 0);
        }
    }

    // Luminosità
    function updateBrightnessUI(): void {
        if (brightnessCounter) brightnessCounter.textContent = brightnessLabels[currentBrightnessStep];
        stepBrightnessBoxes.forEach((el, idx) => {
            el.classList.remove('border-primary');
            if (currentBrightnessStep > 0 && stepToBoxBrightness[currentBrightnessStep] === idx) {
                el.classList.add('border-primary');
            }
        });
        updateBrightnessActiveClass();
        updateToggleIndicator();
    }

    function applyBrightness(): void {
        const html = document.documentElement;
        removeVendorFilterPart(html, /brightness\([^)]+\)/g);
        if (currentBrightnessStep > 0) {
            appendVendorFilter(html, brightnessValues[currentBrightnessStep]);
        }
        const parent = brightnessBtn ? brightnessBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentBrightnessStep > 0);
        }
    }

    function nextBrightnessStep(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        currentBrightnessStep = (currentBrightnessStep + 1) % maxBrightnessSteps;
        updateBrightnessUI();
        applyBrightness();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    function updateBrightnessActiveClass(): void {
        const parent = brightnessBtn ? brightnessBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentBrightnessStep > 0);
        }
    }

    // Contrasto
    function updateContrastUI(): void {
        if (contrastCounter) contrastCounter.textContent = contrastLabels[currentContrastStep];
        stepContrastBoxes.forEach((el, idx) => {
            el.classList.remove('border-primary');
            if (currentContrastStep > 0 && stepToBoxContrast[currentContrastStep] === idx) {
                el.classList.add('border-primary');
            }
        });
        updateContrastActiveClass();
        updateToggleIndicator();
    }

    function applyContrast(): void {
        const html = document.documentElement;
        removeVendorFilterPart(html, /contrast\([^)]+\)/g);
        if (currentContrastStep > 0) {
            appendVendorFilter(html, contrastValues[currentContrastStep]);
        }
        const parent = contrastBtn ? contrastBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentContrastStep > 0);
        }
    }

    function nextContrastStep(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        currentContrastStep = (currentContrastStep + 1) % maxContrastSteps;
        updateContrastUI();
        applyContrast();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    function updateContrastActiveClass(): void {
        const parent = contrastBtn ? contrastBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentContrastStep > 0);
        }
    }

    // Tonalità (Hue-rotate)
    function updateHueUI(): void {
        if (hueCounter) hueCounter.textContent = hueLabels[currentHueStep];
        stepHueBoxes.forEach((el, idx) => {
            el.classList.remove('border-primary');
            if (currentHueStep > 0 && stepToBoxHue[currentHueStep] === idx) {
                el.classList.add('border-primary');
            }
        });
        updateHueActiveClass();
        updateToggleIndicator();
    }

    function applyHue(): void {
        const html = document.documentElement;
        removeVendorFilterPart(html, /hue-rotate\([^)]+\)/g);
        if (currentHueStep > 0) {
            appendVendorFilter(html, hueValues[currentHueStep]);
        }
        const parent = hueBtn ? hueBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentHueStep > 0);
        }
    }

    function nextHueStep(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        currentHueStep = (currentHueStep + 1) % maxHueSteps;
        updateHueUI();
        applyHue();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    function updateHueActiveClass(): void {
        const parent = hueBtn ? hueBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', currentHueStep > 0);
        }
    }

    /* --------------------------------------------------------------------------
       3.11. ANIMAZIONI - Disattiva/riattiva animazioni CSS e JS
    ----------------------------------------------------------------------------*/
    function applyAnimationToggle(): void {
        const html = document.documentElement;
        if (isAnimationDisabled) {
            html.classList.add('aW-disable-animations');
            if (window.AOS && typeof window.AOS.refreshHard === 'function') {
                document.querySelectorAll('[data-aos]').forEach(el => {
                    el.removeAttribute('data-aos');
                });
                window.AOS.refreshHard();
            }
        } else {
            html.classList.remove('aW-disable-animations');
            if (window.AOS && typeof window.AOS.init === 'function') {
                window.AOS.init();
            }
        }
        const parent = animationBtn ? animationBtn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isAnimationDisabled);
        }
    }

    function toggleAnimation(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isAnimationDisabled = !isAnimationDisabled;
        applyAnimationToggle();
        updateToggleIndicator();
        saveAccessibilityState();
    }

    /* --------------------------------------------------------------------------
       3.12. SINTESI VOCALE - Modalità lettura, click su testo/links
    ----------------------------------------------------------------------------*/
    function speakText(text: string): void {
        if (!('speechSynthesis' in window)) {
            alert('La sintesi vocale non è supportata dal tuo browser.');
            return;
        }
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'it-IT';
        window.speechSynthesis.speak(utterance);
    }

    function handleTextClick(e: Event): void {
        if (isReadingModeActive) {
            e.preventDefault();
            e.stopPropagation();
            const target = e.currentTarget as HTMLElement;
            const text = target.getAttribute('aria-label') || (target.textContent || '').trim();
            speakText(text);
        }
    }

    function toggleReadingMode(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        isReadingModeActive = !isReadingModeActive;
        document.body.classList.toggle('aW-reading-mode', isReadingModeActive);
        const btn    = document.getElementById('aW-reading');
        const parent = btn ? btn.closest('.aW-features__item') : null;
        if (parent) {
            parent.classList.toggle('aW-features__item__active', isReadingModeActive);
        }
        if (isReadingModeActive) {
            document.querySelectorAll<HTMLElement>('p, h1, h2, h3, h4, h5, h6, li, a, .aW-readable').forEach(el => {
                el.addEventListener('click', handleTextClick);
                el.classList.add('aW-readable-cursor');
            });
        } else {
            document.querySelectorAll<HTMLElement>('p, h1, h2, h3, h4, h5, h6, li, a, .aW-readable').forEach(el => {
                el.removeEventListener('click', handleTextClick);
                el.classList.remove('aW-readable-cursor');
            });
            window.speechSynthesis.cancel();
        }
        updateToggleIndicator();
        saveAccessibilityState();
    }

    document.getElementById('aW-reading')?.addEventListener('click', function (e) {
        e.preventDefault();
        toggleReadingMode();
    });

    /* --------------------------------------------------------------------------
        3.13 PROFILO DISLESSIA - Attiva/disattiva preset per dislessia
    ----------------------------------------------------------------------------*/
    function toggleDislessiaProfile(): void {
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        if (!isDislessiaProfileActive) {
            resetAccessibilityWidget();
            isAnimationDisabled   = true;
            currentFontFamilyStep = 1;
            currentSpacingStep    = 1;
            currentInterlineStep  = 1;
            applyAnimationToggle();
            updateFontFamilyUI();
            applyFontFamily();
            updateSpacingUI();
            applySpacing();
            updateInterlineUI();
            applyInterline();
            isDislessiaProfileActive = true;
            updateDislessiaProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        } else {
            resetAccessibilityWidget();
            isDislessiaProfileActive = false;
            updateDislessiaProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        }
    }

    function updateDislessiaProfileActiveClass(): void {
        const parent = dislessiaBtn ? dislessiaBtn.closest('.aW-profile__item') : null;
        if (parent) {
            parent.classList.toggle('aW-profile__item__active', isDislessiaProfileActive);
        }
    }

    function deactivateDislessiaProfile(): void {
        if (isDislessiaProfileActive) {
            isDislessiaProfileActive = false;
            updateDislessiaProfileActiveClass();
            saveAccessibilityState();
        }
    }

    /* --------------------------------------------------------------------------
        3.14 PROFILO DALTONISMO - Attiva/disattiva preset per daltonismo
    ----------------------------------------------------------------------------*/
    function toggleDaltonismoProfile(): void {
        deactivateDislessiaProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        if (!isDaltonismoProfileActive) {
            resetAccessibilityWidget();
            currentSaturazioneStep = 1;
            currentContrastStep    = 1;
            currentHueStep         = 5;
            updateSaturazioneUI();
            applySaturazione();
            updateContrastUI();
            applyContrast();
            updateHueUI();
            applyHue();
            isDaltonismoProfileActive = true;
            updateDaltonismoProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        } else {
            resetAccessibilityWidget();
            isDaltonismoProfileActive = false;
            updateDaltonismoProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        }
    }

    function updateDaltonismoProfileActiveClass(): void {
        const parent = daltonismoBtn ? daltonismoBtn.closest('.aW-profile__item') : null;
        if (parent) {
            parent.classList.toggle('aW-profile__item__active', isDaltonismoProfileActive);
        }
    }

    function deactivateDaltonismoProfile(): void {
        if (isDaltonismoProfileActive) {
            isDaltonismoProfileActive = false;
            updateDaltonismoProfileActiveClass();
            saveAccessibilityState();
        }
    }

    /* --------------------------------------------------------------------------
        3.15 PROFILO EPILESSIA - Attiva/disattiva preset per epilessia
    ----------------------------------------------------------------------------*/
    function toggleEpilessiaProfile(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        if (!isEpilessiaProfileActive) {
            resetAccessibilityWidget();
            isAnimationDisabled    = true;
            currentSaturazioneStep = 4;
            applyAnimationToggle();
            updateSaturazioneUI();
            applySaturazione();
            isEpilessiaProfileActive = true;
            updateEpilessiaProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        } else {
            resetAccessibilityWidget();
            isEpilessiaProfileActive = false;
            updateEpilessiaProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        }
    }

    function updateEpilessiaProfileActiveClass(): void {
        const parent = epilessiaBtn ? epilessiaBtn.closest('.aW-profile__item') : null;
        if (parent) {
            parent.classList.toggle('aW-profile__item__active', isEpilessiaProfileActive);
        }
    }

    function deactivateEpilessiaProfile(): void {
        if (isEpilessiaProfileActive) {
            isEpilessiaProfileActive = false;
            updateEpilessiaProfileActiveClass();
            saveAccessibilityState();
        }
    }

    /* --------------------------------------------------------------------------
        3.16 PROFILO ADHD - Attiva/disattiva preset per adhd
    ----------------------------------------------------------------------------*/
    function toggleAdhdProfile(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        if (!isAdhdProfileActive) {
            resetAccessibilityWidget();
            isAnimationDisabled    = true;
            currentSaturazioneStep = 4;
            isReadingMaskActive    = true;
            applyAnimationToggle();
            updateSaturazioneUI();
            applySaturazione();
            applyReadingMask();
            isAdhdProfileActive = true;
            updateAdhdProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        } else {
            resetAccessibilityWidget();
            isAdhdProfileActive = false;
            updateAdhdProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        }
    }

    function updateAdhdProfileActiveClass(): void {
        const parent = adhdBtn ? adhdBtn.closest('.aW-profile__item') : null;
        if (parent) {
            parent.classList.toggle('aW-profile__item__active', isAdhdProfileActive);
        }
    }

    function deactivateAdhdProfile(): void {
        if (isAdhdProfileActive) {
            isAdhdProfileActive = false;
            updateAdhdProfileActiveClass();
            saveAccessibilityState();
        }
    }

    /* --------------------------------------------------------------------------
        3.17 PROFILO IPOVEDENTI - Attiva/disattiva preset per ipovedenti
    ----------------------------------------------------------------------------*/
    function toggleIpovedentiProfile(): void {
        deactivateDislessiaProfile();
        deactivateDaltonismoProfile();
        deactivateEpilessiaProfile();
        deactivateAdhdProfile();
        deactivateIpovedentiProfile();
        if (!isIpovedentiProfileActive) {
            resetAccessibilityWidget();
            isAnimationDisabled    = true;
            currentFontSizeStep    = 3;
            currentFontFamilyStep  = 2;
            isCursorBig            = true;
            currentSaturazioneStep = 1;
            applyAnimationToggle();
            updateFontSizeUI();
            setFontSize();
            updateFontFamilyUI();
            applyFontFamily();
            applyBigCursor();
            updateSaturazioneUI();
            applySaturazione();
            isIpovedentiProfileActive = true;
            updateIpovedentiProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        } else {
            resetAccessibilityWidget();
            isIpovedentiProfileActive = false;
            updateIpovedentiProfileActiveClass();
            updateToggleIndicator();
            saveAccessibilityState();
        }
    }

    function updateIpovedentiProfileActiveClass(): void {
        const parent = ipovedentiBtn ? ipovedentiBtn.closest('.aW-profile__item') : null;
        if (parent) {
            parent.classList.toggle('aW-profile__item__active', isIpovedentiProfileActive);
        }
    }

    function deactivateIpovedentiProfile(): void {
        if (isIpovedentiProfileActive) {
            isIpovedentiProfileActive = false;
            updateIpovedentiProfileActiveClass();
            saveAccessibilityState();
        }
    }

    // --- Reset di tutte le impostazioni ---
    function resetAccessibilityWidget(): void {
        currentFontSizeStep    = 0;
        currentFontFamilyStep  = 0;
        currentSpacingStep     = 0;
        currentInterlineStep   = 0;
        isHighTitleActive      = false;
        isHighLinkActive       = false;
        isHideImgActive        = false;
        isCursorBig            = false;
        isReadingGuideActive   = false;
        isReadingMaskActive    = false;
        isGrayscaleActive      = false;
        isInvertiActive        = false;
        currentSaturazioneStep = 0;
        currentBrightnessStep  = 0;
        currentContrastStep    = 0;
        currentHueStep         = 0;
        isAnimationDisabled    = false;
        isReadingModeActive    = false;

        isDislessiaProfileActive  = false;
        updateDislessiaProfileActiveClass();
        isDaltonismoProfileActive = false;
        updateDaltonismoProfileActiveClass();
        isEpilessiaProfileActive  = false;
        updateEpilessiaProfileActiveClass();
        isAdhdProfileActive       = false;
        updateAdhdProfileActiveClass();
        isIpovedentiProfileActive = false;
        updateIpovedentiProfileActiveClass();

        updateFontSizeUI();
        setFontSize();
        updateFontFamilyUI();
        applyFontFamily();
        updateSpacingUI();
        applySpacing();
        updateInterlineUI();
        applyInterline();
        applyHighlightToTitles();
        applyHighlightToLinks();
        applyHideImg();
        applyBigCursor();
        applyReadingGuide();
        applyReadingMask();
        applyGrayscale();
        applyInverti();
        updateSaturazioneUI();
        applySaturazione();
        updateBrightnessUI();
        applyBrightness();
        updateContrastUI();
        applyContrast();
        updateHueUI();
        applyHue();
        applyAnimationToggle();

        updateToggleIndicator();
        saveAccessibilityState();
    }

    // --- Aggiorna la spunta del toggle principale ---
    function updateToggleIndicator(): void {
        const indicator = document.getElementById('accessibilityToggleIndicator');
        if (!indicator) return;
        const isDefault =
            currentFontSizeStep    === 0 &&
            currentFontFamilyStep  === 0 &&
            currentSpacingStep     === 0 &&
            currentInterlineStep   === 0 &&
            !isHighTitleActive      &&
            !isHighLinkActive       &&
            !isHideImgActive        &&
            !isCursorBig            &&
            !isReadingGuideActive   &&
            !isReadingMaskActive    &&
            !isGrayscaleActive      &&
            !isInvertiActive        &&
            currentSaturazioneStep === 0 &&
            currentBrightnessStep  === 0 &&
            currentContrastStep    === 0 &&
            currentHueStep         === 0 &&
            !isAnimationDisabled    &&
            !isReadingModeActive;
        indicator.classList.toggle('d-none', isDefault);
    }


    // ==============================
    // 4. INIZIALIZZAZIONE WIDGET
    // ==============================

    loadAccessibilityState();
    updateFontSizeUI();
    setFontSize();
    updateFontFamilyUI();
    applyFontFamily();
    updateSpacingUI();
    applySpacing();
    updateInterlineUI();
    applyInterline();
    applyHighlightToTitles();
    applyHighlightToLinks();
    updateToggleIndicator();
    applyHideImg();
    applyBigCursor();
    applyReadingGuide();
    applyReadingMask();
    applyGrayscale();
    applyInverti();
    updateSaturazioneUI();
    applySaturazione();
    updateBrightnessUI();
    applyBrightness();
    updateContrastUI();
    applyContrast();
    updateHueUI();
    applyHue();
    applyAnimationToggle();

    // --- Eventi click sui bottoni ---
    if (increaseFontSizeBtn) increaseFontSizeBtn.addEventListener('click', increaseFontSize);
    if (fontFamilyBtn)       fontFamilyBtn.addEventListener('click', nextFontFamilyStep);
    if (spacingBtn)          spacingBtn.addEventListener('click', nextSpacingStep);
    if (interlineBtn)        interlineBtn.addEventListener('click', nextInterlineStep);
    if (highTitleBtn)        highTitleBtn.addEventListener('click', toggleHighTitle);
    if (highLinkBtn)         highLinkBtn.addEventListener('click', toggleHighLink);
    if (hideImgBtn)          hideImgBtn.addEventListener('click', toggleHideImg);
    if (cursorBtn)           cursorBtn.addEventListener('click', toggleBigCursor);
    if (readingGuideBtn)     readingGuideBtn.addEventListener('click', toggleReadingGuide);
    if (readingMaskBtn)      readingMaskBtn.addEventListener('click', toggleReadingMask);
    if (grayscaleBtn)        grayscaleBtn.addEventListener('click', toggleGrayscale);
    if (invertiBtn)          invertiBtn.addEventListener('click', toggleInverti);
    if (saturazioneBtn)      saturazioneBtn.addEventListener('click', nextSaturazioneStep);
    if (brightnessBtn)       brightnessBtn.addEventListener('click', nextBrightnessStep);
    if (contrastBtn)         contrastBtn.addEventListener('click', nextContrastStep);
    if (hueBtn)              hueBtn.addEventListener('click', nextHueStep);
    if (animationBtn)        animationBtn.addEventListener('click', toggleAnimation);
    if (dislessiaBtn) {
        dislessiaBtn.addEventListener('click', (e) => { e.preventDefault(); toggleDislessiaProfile(); });
    }
    if (daltonismoBtn) {
        daltonismoBtn.addEventListener('click', (e) => { e.preventDefault(); toggleDaltonismoProfile(); });
    }
    if (epilessiaBtn) {
        epilessiaBtn.addEventListener('click', (e) => { e.preventDefault(); toggleEpilessiaProfile(); });
    }
    if (adhdBtn) {
        adhdBtn.addEventListener('click', (e) => { e.preventDefault(); toggleAdhdProfile(); });
    }
    if (ipovedentiBtn) {
        ipovedentiBtn.addEventListener('click', (e) => { e.preventDefault(); toggleIpovedentiProfile(); });
    }


    // ==============================
    // 5. STRUTTURA PAGINA E MODALI
    // ==============================

    const modalPageStructure = document.getElementById('ModalPageStructure');
    if (modalPageStructure) {
        modalPageStructure.addEventListener('show.bs.modal', function () {
            // Titoli
            const headingsTree = buildHeadingsTree();
            const headingsHtml = renderHeadingsTree(headingsTree);
            const titlesPane = document.getElementById('titoli-tab-pane');
            if (titlesPane) titlesPane.innerHTML = headingsHtml || '<p>Nessun titolo trovato.</p>';

            // Gestione click sui link dei titoli
            document.querySelectorAll<HTMLAnchorElement>('.aw-heading-link').forEach(link => {
                link.addEventListener('click', function (e) {
                    e.preventDefault();
                    const idx = (e.currentTarget as HTMLElement).getAttribute('data-aw-heading-idx');
                    const heading = document.querySelector(`[data-aw-heading-idx="${idx}"]`);
                    if (heading) {
                        setTimeout(() => {
                            heading.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            heading.classList.add('aw-heading-highlight');
                            setTimeout(() => heading.classList.remove('aw-heading-highlight'), 1200);
                        }, 300);
                    }
                });
            });

            // Sezioni semantiche
            const semanticTree = buildSemanticTree();
            const semanticHtml = renderSemanticTree(semanticTree);
            const sectionPane = document.getElementById('sezioni-tab-pane');
            if (sectionPane) sectionPane.innerHTML = semanticHtml || '<p>Nessuna sezione semantica trovata.</p>';

            // Gestione click sui link delle sezioni
            document.querySelectorAll<HTMLAnchorElement>('.aw-semantic-link').forEach(link => {
                link.addEventListener('click', function (e) {
                    e.preventDefault();
                    const idx     = (e.currentTarget as HTMLElement).getAttribute('data-aw-semantic-idx');
                    const section = document.querySelector(`[data-aw-semantic-idx="${idx}"]`) as HTMLElement | null;
                    if (section) {
                        const modal = document.getElementById('ModalPageStructure');
                        if (window.bootstrap && window.bootstrap.Modal) {
                            let modalInstance = window.bootstrap.Modal.getInstance(modal!);
                            if (!modalInstance) {
                                modalInstance = new window.bootstrap.Modal(modal!);
                            }
                            modalInstance.hide();
                        }
                        setTimeout(() => {
                            section.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            section.classList.add('aw-section-highlight');
                            setTimeout(() => section.classList.remove('aw-section-highlight'), 1200);
                        }, 300);
                    }
                });
            });

            // Links
            const linksInfo = getAllLinksInfo();
            const linksHtml = renderLinksList(linksInfo);
            const linksPane = document.getElementById('links-tab-pane');
            if (linksPane) linksPane.innerHTML = linksHtml;
        });
    }
}
